<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watchEffect } from 'vue';
import AudioFileIssueCard from '@/components/AudioFileIssues/AudioFileIssueCard.vue';
import AudioFileIssueCardSkeleton from '@/components/AudioFileIssues/AudioFileIssueCardSkeleton.vue';
import AudioFileIssueInspector from '@/components/AudioFileIssueInspector.vue';
import type { AudioFileIssueRow } from '@/composables/api/useAudioFileIssuesApi';

/**
 * Desktop split-view layout for the Audio File Issues page (SAU-164).
 *
 * Replaces the inspector overlay when card mode is active on a wide enough
 * viewport: the card list lives in a scrollable left pane, the right pane
 * renders the {@link AudioFileIssueInspector} inline against the selected
 * track. The vertical divider between them is pointer-draggable; the width is
 * persisted to localStorage so it survives reloads.
 *
 * The mobile fall-through stays in the parent page — this component is only
 * mounted when the parent decides the viewport is wide enough.
 */
const props = defineProps<{
    rows: AudioFileIssueRow[];
    selectedTrackCode: string | null;
    otherIssueTypeLabelsFor: (row: AudioFileIssueRow) => string[];
    /** True while the upstream list query is loading; used for the empty-state copy. */
    isLoading?: boolean;
}>();

const emit = defineEmits<{
    (e: 'select', row: AudioFileIssueRow, event?: MouseEvent): void;
}>();

const STORAGE_KEY = 'audio-file-issues-split-detail-width';
const DEFAULT_WIDTH = 520;
const MIN_WIDTH = 320;
const MIN_LIST_WIDTH = 320;

const detailWidth = ref(DEFAULT_WIDTH);
const containerRef = ref<HTMLElement | null>(null);
const isResizing = ref(false);

function loadStoredWidth() {
    if (typeof localStorage === 'undefined') return;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    const parsed = Number(stored);
    if (Number.isFinite(parsed) && parsed >= MIN_WIDTH) {
        detailWidth.value = parsed;
    }
}
loadStoredWidth();

function clampToContainer(candidate: number): number {
    const container = containerRef.value;
    if (!container) {
        return Math.max(MIN_WIDTH, candidate);
    }
    const ceiling = Math.max(MIN_WIDTH, container.clientWidth - MIN_LIST_WIDTH);
    return Math.max(MIN_WIDTH, Math.min(ceiling, candidate));
}

const effectiveDetailWidth = computed(() => clampToContainer(detailWidth.value));

let resizeStartX = 0;
let resizeStartWidth = 0;

function onResizeStart(event: PointerEvent) {
    event.preventDefault();
    isResizing.value = true;
    resizeStartX = event.clientX;
    resizeStartWidth = effectiveDetailWidth.value;
    document.addEventListener('pointermove', onResizeMove);
    document.addEventListener('pointerup', onResizeEnd);
}

function onResizeMove(event: PointerEvent) {
    if (!isResizing.value) return;
    const delta = resizeStartX - event.clientX;
    detailWidth.value = clampToContainer(resizeStartWidth + delta);
}

function onResizeEnd() {
    if (!isResizing.value) return;
    isResizing.value = false;
    if (typeof localStorage !== 'undefined') {
        try {
            localStorage.setItem(STORAGE_KEY, String(Math.round(detailWidth.value)));
        } catch {
            // Storage may be unavailable — best-effort only.
        }
    }
    document.removeEventListener('pointermove', onResizeMove);
    document.removeEventListener('pointerup', onResizeEnd);
}

onBeforeUnmount(() => {
    document.removeEventListener('pointermove', onResizeMove);
    document.removeEventListener('pointerup', onResizeEnd);
});

watchEffect(() => {
    if (typeof document === 'undefined') return;
    if (isResizing.value) {
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    } else {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }
});

function onCardClick(row: AudioFileIssueRow, event: MouseEvent) {
    emit('select', row, event);
}

const showEmptyState = computed(
    () => !props.isLoading && props.rows.length === 0,
);
</script>

<template>
    <div
        ref="containerRef"
        data-testid="audio-file-issues-split-view"
        class="flex h-full w-full min-h-0"
    >
        <!-- Left pane: scrollable card list. min-w-0 so flex-basis can shrink past content width.
             `relative`, so anything absolutely positioned inside a card (an `sr-only`
             label is) is clipped by this pane rather than escaping to the layout and
             stretching the page. See SearchCardsSplitView for the case that did. -->
        <div
            data-testid="audio-file-issues-split-list"
            class="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-auto"
        >
            <!--
              Cards fill the pane width (no max-width cap — the detail pane already
              constrains it) and stop a card-width's gap short of the divider.
              Symmetric px-3 keeps each card's shadow off the scroll-clip edges.
            -->
            <div class="flex w-full flex-col gap-3 px-3 pt-2 pb-4">
                <template v-if="isLoading && rows.length === 0">
                    <AudioFileIssueCardSkeleton v-for="i in 8" :key="`skeleton-${i}`" />
                </template>
                <AudioFileIssueCard
                    v-for="row in rows"
                    :key="`${row.live_track_code}-${row.id}`"
                    :row="row"
                    :other-issue-type-labels="otherIssueTypeLabelsFor(row)"
                    :active="row.live_track_code === selectedTrackCode"
                    @click="onCardClick"
                />
                <div
                    v-if="showEmptyState"
                    class="text-muted-foreground py-8 text-center text-sm"
                >
                    No audio file issues match the current filters.
                </div>
            </div>
        </div>

        <!-- Resize handle. Pointer-only; keyboard users can switch to row mode. -->
        <div
            data-testid="audio-file-issues-split-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize detail pane"
            class="relative w-1.5 shrink-0 cursor-col-resize bg-border/60 hover:bg-primary/40 active:bg-primary/60 transition-colors"
            @pointerdown="onResizeStart"
        />

        <!-- Right pane: inline inspector for the selected track. `relative` for the
             same reason as the list pane. -->
        <div
            data-testid="audio-file-issues-split-detail"
            class="relative flex min-h-0 shrink-0 flex-col overflow-auto border-l bg-background"
            :style="{ width: `${effectiveDetailWidth}px` }"
        >
            <div v-if="selectedTrackCode" class="px-5 py-6">
                <AudioFileIssueInspector :track-code="selectedTrackCode" />
            </div>
            <div
                v-else
                data-testid="audio-file-issues-split-detail-empty"
                class="text-muted-foreground flex flex-1 items-center justify-center px-5 py-6 text-center text-sm"
            >
                Select a card to inspect the track.
            </div>
        </div>
    </div>
</template>
